import { default as React } from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

/** Form value type for GitHubRepoUrlInput field */
type GitHubRepoUrlFormValue = {
  value?: string;
  error?: string;
  detectedPort?: number;
  /** Default branch resolved from the repo — passed to build actions to avoid a redundant GitHub API call */
  branch?: string;
};

/**
 * Parsed result of a GitHub URL.
 * Mirrors ParsedGitHubUrl from the backend utils/github.ts.
 */
interface ParsedGitHubUrl {
  owner: string;
  repo: string;
  branch: string | null;
  subfolder: string | null;
}

/**
 * Parses a GitHub repository URL into its components.
 *
 * Supports both root repo URLs and monorepo subfolder URLs:
 *   Root:      https://github.com/org/repo
 *   Subfolder: https://github.com/org/repo/tree/main/apps/my-app
 *
 * Returns null if the URL is not a valid GitHub URL.
 */
function parseGitHubUrlFrontend(githubRepoUrl: string): ParsedGitHubUrl | null {
  const trimmed = githubRepoUrl.trim();
  if (!trimmed) return null;

  // Normalize SSH to HTTPS
  const normalized = trimmed.startsWith('git@')
    ? trimmed.replace(/^git@github\.com:/, 'https://github.com/')
    : trimmed;

  let url: URL;
  try {
    url = new URL(normalized);
  } catch {
    return null;
  }

  if (url.hostname.toLowerCase() !== 'github.com') return null;

  const parts = url.pathname
    .replace(/^\//, '')
    .replace(/\.git$/, '')
    .split('/');

  if (parts.length < 2 || !parts[0] || !parts[1]) return null;

  const owner = parts[0];
  const repo = parts[1];

  // Subfolder URL: /org/repo/tree/{branch}/{subfolder...}
  // Also accept /blob/ — GitHub uses the same path structure for both directory
  // (/tree/) and file (/blob/) views, so treat them identically.
  if (parts.length >= 4 && (parts[2] === 'tree' || parts[2] === 'blob')) {
    const branch = parts[3];
    const subfolder = parts.length > 4 ? parts.slice(4).join('/') : null;
    return { owner, repo, branch, subfolder };
  }

  // Root URL (extra path segments that aren't 'tree' or 'blob' are not valid)
  if (parts.length > 2 && parts[2] !== 'tree' && parts[2] !== 'blob')
    return null;

  return { owner, repo, branch: null, subfolder: null };
}

/**
 * Extracts a Kubernetes-compliant application name from a GitHub repository URL.
 *
 * IMPORTANT: This mirrors the backend logic in:
 * plugins/scaffolder-backend-module-quick-projects/src/utils/github.ts -> extractAppName()
 *
 * For monorepo subfolder URLs, the leaf subfolder segment is used as the app
 * name component (e.g., /apps/my-app → 'my-app'), giving each subfolder app
 * a unique, meaningful name.
 *
 * If you add a new GitHub organization or change an org alias, you MUST update BOTH files.
 * The orgMapping below must stay in sync with the backend version.
 */
function extractAppNameFromUrl(githubRepoUrl: string): string | null {
  const parsed = parseGitHubUrlFrontend(githubRepoUrl);
  if (!parsed) return null;

  const orgMapping: Record<string, string> = {
    'wsky-test': 'wst',
    mediwareinc: 'minc',
  };

  const originalOrg = parsed.owner.toLowerCase();
  const mappedOrg = orgMapping[originalOrg] || originalOrg;

  // For monorepo subfolder URLs, use the subfolder leaf as the app name
  let appPart: string;
  if (parsed.subfolder) {
    const subfolderParts = parsed.subfolder.split('/').filter(Boolean);
    if (subfolderParts.length === 0) return null;
    appPart = subfolderParts[subfolderParts.length - 1].toLowerCase();
  } else {
    appPart = parsed.repo.toLowerCase();
  }

  const name = `${appPart}-${mappedOrg}`
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-')
    .slice(0, 63);

  if (!/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name)) {
    return null;
  }

  return name;
}

/**
 * Extracts app name for public app hosting — no org suffix.
 * Mirrors the backend extractPublicAppName() in utils/github.ts.
 * Use this when allowedOrganizations is set (public-app-hosting template).
 */
function extractPublicAppNameFromUrl(githubRepoUrl: string): string | null {
  const parsed = parseGitHubUrlFrontend(githubRepoUrl);
  if (!parsed) return null;

  let appPart: string;
  if (parsed.subfolder) {
    const subfolderParts = parsed.subfolder.split('/').filter(Boolean);
    if (subfolderParts.length === 0) return null;
    appPart = subfolderParts[subfolderParts.length - 1].toLowerCase();
  } else {
    appPart = parsed.repo.toLowerCase();
  }

  const name = appPart
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-')
    .slice(0, 63);

  return /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name) ? name : null;
}

/**
 * Custom GitHub Repo URL input field for Microapp Hosting template.
 *
 * Features:
 * - Can be disabled for redeploy operations (actionType === 'redeploy')
 * - Checks if the app derived from the GitHub URL already exists in the catalog
 * - Shows an info alert with a link to the existing catalog entity if deployed
 */
export const GitHubRepoUrlInput = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<GitHubRepoUrlFormValue>) => {
  // Extract URL value from form data - handle both string (legacy/redeploy) and object formats
  const value = typeof formData === 'string' ? formData : formData?.value || '';
  const configApi = useApi(configApiRef);
  // Org restriction: ui:options.allowedOrganizations takes precedence; falls back
  // to publicAppHosting.allowedGithubOrganizations from app-config (set per environment).
  const allowedOrgs = useMemo<string[]>(
    () =>
      (uiSchema?.['ui:options']?.allowedOrganizations as
        | string[]
        | undefined) ??
      configApi.getOptionalStringArray(
        'publicAppHosting.allowedGithubOrganizations',
      ) ??
      [],
    [uiSchema, configApi],
  );
  const fetchApi = useApi(fetchApiRef);

  // Use ref for onChange to avoid infinite re-renders in useEffect dependencies
  // Backstage's onChange may not be stable across renders
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [checking, setChecking] = useState(false);
  const [invalidOrg, setInvalidOrg] = useState(false);
  const [existingApp, setExistingApp] = useState<{
    name: string;
    title: string;
    catalogUrl: string;
  } | null>(null);

  // State for Dockerfile EXPOSE port check
  const [dockerfilePort, setDockerfilePort] = useState<number | null>(null);
  const [checkingPort, setCheckingPort] = useState(false);
  // Track if we've auto-set the port (to detect user changes)
  const [autoSetPort, setAutoSetPort] = useState<number | null>(null);

  // Check if this is a redeploy operation from formContext
  const isRedeploy = formContext?.formData?.actionType === 'redeploy';

  // Read containerPort from form context
  const rawContainerPort = formContext?.formData?.containerPort;
  const getContainerPort = (): number => {
    if (typeof rawContainerPort === 'number') {
      return rawContainerPort;
    }
    if (typeof rawContainerPort === 'string' && rawContainerPort !== '') {
      const parsed = parseInt(rawContainerPort, 10);
      return Number.isNaN(parsed) ? 3001 : parsed;
    }
    return 3001;
  };
  const containerPort: number = getContainerPort();

  // Detect if user has manually changed the port from the auto-detected value
  const userChangedPort = autoSetPort !== null && containerPort !== autoSetPort;

  // Update form value with error state when existingApp changes
  useEffect(() => {
    if (existingApp && !isRedeploy) {
      onChange({
        value,
        error: `This repository is already deployed as "${existingApp.title}". View it in the catalog to redeploy.`,
      });
    } else if (value && !existingApp && formData?.error) {
      // Clear error when app no longer exists
      onChange({ value, error: undefined });
    }
  }, [existingApp, value, isRedeploy, onChange, formData?.error]);

  // Check if app already exists in catalog when GitHub URL changes
  useEffect(() => {
    if (!value || isRedeploy) {
      setInvalidOrg(false);
      setExistingApp(null);
      return undefined;
    }

    const parsed = parseGitHubUrlFrontend(value);
    if (
      parsed &&
      allowedOrgs.length > 0 &&
      !allowedOrgs
        .map(o => o.toLowerCase())
        .includes(parsed.owner.toLowerCase())
    ) {
      setInvalidOrg(true);
      setExistingApp(null);
      onChangeRef.current({
        value,
        error: `Only repositories from the ${allowedOrgs.join(
          ' or ',
        )} organization are allowed. Found: '${parsed.owner}'.`,
      });
      return undefined;
    }
    setInvalidOrg(false);

    const appName =
      allowedOrgs.length > 0
        ? extractPublicAppNameFromUrl(value)
        : extractAppNameFromUrl(value);
    if (!appName) {
      setExistingApp(null);
      return undefined;
    }

    setChecking(true);
    setExistingApp(null);

    const timeoutId = setTimeout(async () => {
      try {
        const backendUrl = configApi.getString('backend.baseUrl');
        const baseUrl =
          configApi.getOptionalString('app.baseUrl') || window.location.origin;
        // Use lightweight endpoint that returns only required fields
        const checkUrl = `${backendUrl}/api/microapps-api/check-app-exists/${encodeURIComponent(
          appName,
        )}`;

        const response = await fetchApi.fetch(checkUrl);

        if (response.ok) {
          const data = await response.json();

          if (data.exists) {
            const catalogUrl = `${baseUrl}/catalog/default/component/${encodeURIComponent(
              data.name || '',
            )}`;
            setExistingApp({
              name: data.name,
              title: data.title,
              catalogUrl,
            });
          } else {
            setExistingApp(null);
          }
        } else {
          setExistingApp(null);
        }
      } catch {
        // Silently ignore errors -- don't block the user
        setExistingApp(null);
      } finally {
        setChecking(false);
      }
    }, 600);

    return () => {
      clearTimeout(timeoutId);
      setChecking(false);
    };
    // Note: Using onChangeRef to avoid re-running effect when onChange reference changes
  }, [value, isRedeploy, allowedOrgs, configApi, fetchApi]);

  // Check Dockerfile EXPOSE port when GitHub URL changes
  useEffect(() => {
    if (!value) {
      setDockerfilePort(null);
      setAutoSetPort(null);
      return undefined;
    }

    // Validate GitHub URL pattern before making API call
    // Supports both root URLs and subfolder URLs
    const parsedForPort = parseGitHubUrlFrontend(value);
    if (!parsedForPort) {
      setDockerfilePort(null);
      return undefined;
    }

    setCheckingPort(true);
    setDockerfilePort(null);

    const timeoutId = setTimeout(async () => {
      try {
        const backendUrl = configApi.getString('backend.baseUrl');
        const checkUrl = `${backendUrl}/api/microapps-api/get-dockerfile-expose-port?repoUrl=${encodeURIComponent(
          value,
        )}`;

        const response = await fetchApi.fetch(checkUrl);

        if (response.ok) {
          const data = await response.json();
          const resolvedBranch: string | undefined = data.branch ?? undefined;
          if (data.found && data.port) {
            setDockerfilePort(data.port);
            onChangeRef.current({
              value,
              detectedPort: data.port,
              branch: resolvedBranch,
            });
          } else {
            setDockerfilePort(null);
            onChangeRef.current({
              value,
              detectedPort: undefined,
              branch: resolvedBranch,
            });
          }
        } else {
          setDockerfilePort(null);
        }
      } catch {
        // Silently ignore errors -- don't block the user
        setDockerfilePort(null);
      } finally {
        setCheckingPort(false);
      }
    }, 600);

    return () => {
      clearTimeout(timeoutId);
      setCheckingPort(false);
    };
    // Note: Using onChangeRef to avoid re-running effect when onChange reference changes
  }, [value, configApi, fetchApi]);

  /**
   * Auto-update containerPort when dockerfile port is detected (for new deployments).
   *
   * WORKAROUND: Backstage scaffolder's FieldExtensionComponentProps doesn't expose
   * a way to update sibling form fields. The official approach would be to use
   * `ui:autofill` in uiSchema or handle this at the template level, but those
   * don't support dynamic values from API responses.
   *
   * This uses DOM manipulation to find and update the containerPort input field.
   * Limitations:
   * - Selector may not match if Backstage changes attribute generation
   * - If the field hasn't rendered yet, the update silently fails (safe fallback)
   * - User can still manually override the auto-set value
   */
  useEffect(() => {
    if (dockerfilePort === null) return;

    // Only auto-set for new deployments, not redeployments
    // And only if containerPort is still at the template default (3001)
    const shouldAutoSet = !isRedeploy && containerPort === 3001;

    if (shouldAutoSet && dockerfilePort !== 3001) {
      // Find the containerPort input field and update it
      // Using multiple selectors for robustness against Backstage attribute changes
      const containerPortInput = document.querySelector(
        'input[id*="containerPort"], input[name*="containerPort"]',
      ) as HTMLInputElement | null;

      if (containerPortInput) {
        // Trigger a React-compatible change event using native setter
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        )?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(containerPortInput, dockerfilePort);
          containerPortInput.dispatchEvent(
            new Event('input', { bubbles: true }),
          );
          // Track that we auto-set this value to detect user changes later
          setAutoSetPort(dockerfilePort);
        }
      }
      // Note: If containerPortInput is null, we silently fail - the user can
      // still manually enter the correct port, and the warning will guide them
    }
  }, [dockerfilePort, isRedeploy, containerPort]);

  // Check if URL is a valid GitHub URL (for conditional message display)
  // Supports both root URLs and subfolder URLs
  const isValidGitHubUrl = parseGitHubUrlFrontend(value);

  // Compute port mismatch - show warning when:
  // - For new deployments: only after user manually changes from auto-detected port
  // - For redeployments: when existing port differs from detected port
  const hasPortDifference =
    dockerfilePort !== null && containerPort !== dockerfilePort;
  const portMismatch = isRedeploy
    ? hasPortDifference
    : hasPortDifference && userChangedPort;

  const getHelperText = () => {
    if (isRedeploy) {
      return 'Repository URL cannot be changed during redeploy';
    }
    if (checking || checkingPort) {
      return checking
        ? 'Checking for existing deployment...'
        : 'Checking Dockerfile EXPOSE port...';
    }
    if (rawErrors?.length) {
      return rawErrors[0];
    }
    return (
      schema.description ||
      'Enter the full GitHub repository URL (e.g., https://github.com/org/repo) or a monorepo subfolder URL (e.g., https://github.com/org/repo/tree/main/apps/my-app)'
    );
  };

  return (
    <Box>
      <TextField
        label={schema.title || 'GitHub Repo URL'}
        placeholder="https://github.com/mediwareinc/your-repo  or  https://github.com/mediwareinc/monorepo/tree/main/apps/my-app"
        value={value}
        onChange={e => onChange({ value: e.target.value })}
        required={required}
        error={rawErrors?.length > 0}
        helperText={getHelperText()}
        disabled={isRedeploy}
        fullWidth
        variant="outlined"
        margin="normal"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment:
            checking || checkingPort ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined,
        }}
      />
      {invalidOrg && allowedOrgs.length > 0 && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Only repositories from the <strong>{allowedOrgs.join(' or ')}</strong>{' '}
          organization are allowed.
        </Alert>
      )}
      {existingApp && !invalidOrg && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span>
              This repository has already been deployed as{' '}
              <strong>{existingApp.title}</strong>.
            </span>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
            >
              <Button
                variant="contained"
                size="small"
                color="primary"
                href={existingApp.catalogUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                sx={{ fontWeight: 600 }}
              >
                View in Catalog
              </Button>
              <span style={{ fontSize: '0.875rem' }}>
                to redeploy if you have access.
              </span>
            </Box>
          </Box>
        </Alert>
      )}
      {dockerfilePort !== null && !portMismatch && !isRedeploy && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Detected <strong>EXPOSE {dockerfilePort}</strong> in Dockerfile.
          Container Port has been set to {dockerfilePort}.
        </Alert>
      )}
      {dockerfilePort === null &&
        !checkingPort &&
        isValidGitHubUrl &&
        !isRedeploy && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No <strong>EXPOSE</strong> port detected in Dockerfile. Using
            default port <strong>3001</strong>. Make sure your application
            listens on this port, or update the Container Port field below.
          </Alert>
        )}
      {portMismatch && !existingApp && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          The Dockerfile in this repository uses{' '}
          <strong>EXPOSE {dockerfilePort}</strong>, but the Container Port is
          set to <strong>{containerPort}</strong>.
          {isRedeploy
            ? ' Consider updating the Container Port to match the Dockerfile, or ensure your application listens on the configured port.'
            : ' You can continue if your application listens on this port. Otherwise, mismatched ports may cause your application to be unreachable after deployment.'}
        </Alert>
      )}
    </Box>
  );
};
